import { SearchOption } from '@broker-crm-hooks/useGlobalSearch'
import { Box } from '@mui/material'
import { FolderOpen, User } from '@phosphor-icons/react'
import isEmpty from 'lodash/isEmpty'
import type { HTMLAttributes } from 'react'
import { FormattedMessage } from 'react-intl'

import { MortgageReason, SearchResultCase, SearchResultItem } from '@acre/graphql'
import { CaseStatusTag, useFormatMessage } from '@acre/design-system'

import { getDisplayedProperty, getName, getProductTypeIntl } from './GlobalSearchOption.helpers'
import {
  AdvisorBox,
  CaseContainer,
  CaseStatusBox,
  CaseTypeBox,
  ClientContainer,
  ClientNamesBox,
  EmailBox,
  PhoneBox,
  PostcodeBox,
  TargetPropertyBox,
} from './GlobalSearchOption.styles'

export const DEFAULT_EMPTY_OPTION = '-'

/**
 * Main GlobalSearchOption component
 *  - handles the case when option is empty and renders a "not found" message
 *  - otherwise renders the GlobalSearchResultOption when an option exist
 */
const GlobalSearchOption = (props: HTMLAttributes<HTMLLIElement>, option: any): JSX.Element => {
  if (isEmpty(option)) {
    return (
      <Box key="option-no-results">
        <FormattedMessage id="generic.nothingToShow" />
      </Box>
    )
  }
  return <GlobalSearchResultOption {...props} {...option} key={`option-${props.id}`} />
}

/**
 * Renders either client or case information based on the item type
 */
const GlobalSearchResultOption = (props: SearchOption & { overrideName?: string; focused?: boolean }) => {
  const { overrideName, doctype, focused, ...rest } = props
  const formatMessage = useFormatMessage()

  // Render client item
  if (doctype === 'client') {
    const clientItem = props as SearchResultItem
    const { organisation_name, first_name, last_name, email, postcode, phone_number, owning_org_name } = clientItem

    return (
      <ClientContainer {...rest} focused={focused}>
        <ClientNamesBox>
          <User size={16} weight="bold" id="UserIcon" />
          {overrideName ?? getName(organisation_name, first_name, last_name)}
        </ClientNamesBox>
        <EmailBox>{email ?? DEFAULT_EMPTY_OPTION}</EmailBox>
        <PostcodeBox>{`${formatMessage('nav.search.address')} ${postcode ?? DEFAULT_EMPTY_OPTION}`}</PostcodeBox>
        <PhoneBox>{phone_number?.[0] ?? DEFAULT_EMPTY_OPTION}</PhoneBox>
        <AdvisorBox>{owning_org_name ?? DEFAULT_EMPTY_OPTION}</AdvisorBox>
      </ClientContainer>
    )
  }

  // Render case item
  if (doctype === 'client_case') {
    const caseItem = props as SearchResultCase
    return (
      <CaseContainer {...rest} focused={focused}>
        <CaseTypeBox>
          <FolderOpen size={16} weight="bold" />
          <FormattedMessage id={getProductTypeIntl(caseItem.case_reason || MortgageReason.InvalidMortgageReason)} />
        </CaseTypeBox>
        <CaseStatusBox>
          <CaseStatusTag status={caseItem.case_status} key={`status_tag_${caseItem.id}`} />
        </CaseStatusBox>
        <TargetPropertyBox>{`${formatMessage('nav.search.property')} ${getDisplayedProperty(caseItem)}`}</TargetPropertyBox>
      </CaseContainer>
    )
  }
}

export default GlobalSearchOption
